import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Users, Phone, Mail, Calendar, CheckCircle, XCircle, Eye, DollarSign, Plane, Lock, Unlock } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDuties, setAgentDuties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [dutiesOpen, setDutiesOpen] = useState(false);
  const [costsOpen, setCostsOpen] = useState(false);
  const [agentCosts, setAgentCosts] = useState(null);
  const [formData, setFormData] = useState({ 
    name: "", 
    phone: "", 
    email: "", 
    password: "",
    role: "staff",
    agency_id: "",
    cost_per_flight: "" 
  });
  const [editingAgent, setEditingAgent] = useState(null);
  const [editPasswordUser, setEditPasswordUser] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [hidePaidFlights, setHidePaidFlights] = useState(false);
  const [staffCosts, setStaffCosts] = useState(null);
  const [staffCostsLoading, setStaffCostsLoading] = useState(false);
  const [agentFlightCounts, setAgentFlightCounts] = useState({});
  const [showPastDuties, setShowPastDuties] = useState(false);
  const [showFutureDuties, setShowFutureDuties] = useState(true);
  
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isStaff = currentUser.role === 'staff';
  const isAdmin = currentUser.role === 'admin';

  useEffect(() => {
    fetchAgents();
    fetchAgencies();
    if (currentUser.role === 'admin') {
      fetchPendingUsers();
    }
    // If staff user, fetch their own costs
    if (isStaff) {
      fetchStaffCosts();
    }
  }, []);

  // Fetch staff's own costs based on their linked agent
  const fetchStaffCosts = async () => {
    setStaffCostsLoading(true);
    try {
      const token = localStorage.getItem('token');
      // First find the agent linked to this user
      const agentsResponse = await axios.get(`${API}/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Find agent by:
      // 1. user_id matches current user's id
      // 2. OR email matches current user's email
      // 3. OR name matches current user's full_name
      const myAgent = agentsResponse.data.find(a => 
        a.user_id === currentUser.id ||
        (a.email && currentUser.email && a.email.toLowerCase() === currentUser.email.toLowerCase()) ||
        (a.name && currentUser.full_name && a.name.toLowerCase() === currentUser.full_name.toLowerCase())
      );
      
      if (myAgent) {
        const costsResponse = await axios.get(`${API}/agents/${myAgent.id}/costs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Sort flights by date
        const sortedFlights = [...costsResponse.data.flights].sort((a, b) => {
          const parseDate = (dateStr) => {
            if (!dateStr) return new Date(0);
            if (dateStr.includes('/')) {
              const [day, month, year] = dateStr.split('/');
              return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            }
            return new Date(dateStr);
          };
          
          const dateA = parseDate(a.flight_date);
          const dateB = parseDate(b.flight_date);
          
          if (dateA.getTime() !== dateB.getTime()) {
            return dateA.getTime() - dateB.getTime();
          }
          
          const timeA = a.flight_time || '00:00';
          const timeB = b.flight_time || '00:00';
          return timeA.localeCompare(timeB);
        });
        
        setStaffCosts({
          ...costsResponse.data,
          flights: sortedFlights
        });
      }
    } catch (error) {
      console.error("Failed to fetch staff costs:", error);
    }
    setStaffCostsLoading(false);
  };

  // Get filtered staff flights based on hide paid setting
  const getFilteredStaffFlights = () => {
    if (!staffCosts) return [];
    if (hidePaidFlights) {
      return staffCosts.flights.filter(f => f.paid_amount < f.to_be_paid);
    }
    return staffCosts.flights;
  };

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(response.data);
      
      // Fetch flight counts for all agents (admin only)
      if (currentUser.role === 'admin') {
        const counts = {};
        await Promise.all(response.data.map(async (agent) => {
          try {
            const countResponse = await axios.get(`${API}/agents/${agent.id}/flight-count`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            counts[agent.id] = countResponse.data.flight_count;
          } catch (error) {
            counts[agent.id] = 0; // Default to 0 on error
          }
        }));
        setAgentFlightCounts(counts);
      }
    } catch (error) {
      toast.error("Failed to fetch agents");
    }
    setLoading(false);
  };
  
  const fetchAgencies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(response.data);
    } catch (error) {
      console.error("Failed to fetch agencies");
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch pending users");
    }
  };

  const fetchAgentDuties = async (agentId, agentName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agents/${agentId}/duties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentDuties(response.data);
      setSelectedAgent(agentName);
      // Reset filters: show future by default
      setShowPastDuties(false);
      setShowFutureDuties(true);
      setDutiesOpen(true);
    } catch (error) {
      toast.error("Failed to fetch agent duties");
    }
  };

  // Helper to parse DD/MM/YYYY date and check if past or future
  const parseDutyDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      if (dateStr.includes('/')) {
        const [day, month, year] = dateStr.split('/');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
    } catch (e) {}
    return null;
  };

  // Filter duties based on past/future checkboxes
  const getFilteredDuties = () => {
    if (!agentDuties || agentDuties.length === 0) return [];
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return agentDuties.filter(duty => {
      const dutyDate = parseDutyDate(duty.flight_date);
      if (!dutyDate) return showFutureDuties; // If can't parse, show with future
      
      const isPast = dutyDate < today;
      const isFuture = dutyDate >= today;
      
      if (showPastDuties && showFutureDuties) return true;
      if (showPastDuties && isPast) return true;
      if (showFutureDuties && isFuture) return true;
      return false;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingAgent ? `${API}/agents/${editingAgent}` : `${API}/agents`;
      const method = editingAgent ? 'put' : 'post';
      
      const submitData = { 
        name: formData.name, 
        phone: formData.phone, 
        email: formData.email,
        role: formData.role,
        agency_id: formData.agency_id || null,
        cost_per_flight: formData.cost_per_flight ? parseFloat(formData.cost_per_flight) : null
      };
      
      // Include password for new agents or when updating password
      if (formData.password) {
        submitData.password = formData.password;
      }
      
      await axios[method](url, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(editingAgent ? "Agent updated" : "Agent created");
      setOpen(false);
      setEditingAgent(null);
      setFormData({ name: "", phone: "", email: "", password: "", role: "staff", agency_id: "", cost_per_flight: "" });
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save agent");
    }
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent.id);
    setFormData({
      name: agent.name,
      phone: agent.phone,
      email: agent.email || "",
      password: "",  // Empty for editing - optional update
      role: agent.role || "staff",
      agency_id: agent.agency_id || "",
      cost_per_flight: agent.cost_per_flight || ""
    });
    setOpen(true);
  };

  // Fetch agent costs
  const fetchAgentCosts = async (agentId, agentName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agents/${agentId}/costs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort flights by date (ascending) then by time
      const sortedFlights = [...response.data.flights].sort((a, b) => {
        // Parse DD/MM/YYYY format
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          if (dateStr.includes('/')) {
            const [day, month, year] = dateStr.split('/');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          }
          return new Date(dateStr);
        };
        
        const dateA = parseDate(a.flight_date);
        const dateB = parseDate(b.flight_date);
        
        // Compare dates first
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA.getTime() - dateB.getTime(); // Ascending (oldest first)
        }
        
        // If dates are the same, compare times
        const timeA = a.flight_time || '00:00';
        const timeB = b.flight_time || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      setAgentCosts({
        ...response.data,
        flights: sortedFlights
      });
      setSelectedAgent(agentName);
      setHidePaidFlights(false); // Reset filter when opening new agent
      setCostsOpen(true);
    } catch (error) {
      toast.error("Failed to fetch agent costs");
    }
  };

  // Update paid amount for a flight
  const updatePaidAmount = async (flightId, paidAmount) => {
    if (!agentCosts) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/agents/${agentCosts.agent_id}/costs/${flightId}`,
        { paid_amount: parseFloat(paidAmount) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh the costs data
      const response = await axios.get(`${API}/agents/${agentCosts.agent_id}/costs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort flights by date (ascending) then by time
      const sortedFlights = [...response.data.flights].sort((a, b) => {
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          if (dateStr.includes('/')) {
            const [day, month, year] = dateStr.split('/');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          }
          return new Date(dateStr);
        };
        
        const dateA = parseDate(a.flight_date);
        const dateB = parseDate(b.flight_date);
        
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA.getTime() - dateB.getTime();
        }
        
        const timeA = a.flight_time || '00:00';
        const timeB = b.flight_time || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      setAgentCosts({
        ...response.data,
        flights: sortedFlights
      });
      toast.success("Payment updated");
    } catch (error) {
      toast.error("Failed to update payment");
    }
  };

  // Toggle fix/unfix price for a flight
  const toggleFixPrice = async (flightId, currentlyFixed, currentPrice) => {
    if (!agentCosts) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/agents/${agentCosts.agent_id}/costs/${flightId}`,
        { 
          is_fixed: !currentlyFixed,
          fixed_price: !currentlyFixed ? currentPrice : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh the costs data
      const response = await axios.get(`${API}/agents/${agentCosts.agent_id}/costs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sort flights by date (ascending) then by time
      const sortedFlights = [...response.data.flights].sort((a, b) => {
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          if (dateStr.includes('/')) {
            const [day, month, year] = dateStr.split('/');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          }
          return new Date(dateStr);
        };
        
        const dateA = parseDate(a.flight_date);
        const dateB = parseDate(b.flight_date);
        
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA.getTime() - dateB.getTime();
        }
        
        const timeA = a.flight_time || '00:00';
        const timeB = b.flight_time || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      setAgentCosts({
        ...response.data,
        flights: sortedFlights
      });
      toast.success(!currentlyFixed ? "Price fixed" : "Price unfixed");
    } catch (error) {
      toast.error("Failed to update price");
    }
  };

  // Get filtered flights based on hide paid setting
  const getFilteredFlights = () => {
    if (!agentCosts) return [];
    if (hidePaidFlights) {
      // Hide fully paid flights (where paid_amount >= to_be_paid)
      return agentCosts.flights.filter(f => f.paid_amount < f.to_be_paid);
    }
    return agentCosts.flights;
  };

  // Format date to DD/MM/YYYY
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    
    // If already in DD/MM/YYYY format, return as is
    if (dateStr.includes('/')) {
      return dateStr;
    }
    
    // If in YYYY-MM-DD format, convert to DD/MM/YYYY
    if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0].length === 4) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
    }
    
    return dateStr;
  };

  // Format time to HH:MM (remove seconds)
  const formatTimeDisplay = (timeStr) => {
    if (!timeStr) return '-';
    // Extract only HH:MM from HH:MM:SS or HH:MM format
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return timeStr;
  };

  const handleApproveUser = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/users/${user.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Open Outlook with pre-filled email
      const mailtoLink = `mailto:${response.data.email_to}?subject=${encodeURIComponent(response.data.email_subject)}&body=${encodeURIComponent(response.data.email_body)}`;
      window.location.href = mailtoLink;
      
      toast.success("User approved! Outlook email opened.");
      fetchPendingUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve user");
    }
  };

  const handleRejectUser = async (userId) => {
    if (!window.confirm('Are you sure you want to reject this staff registration?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/${userId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User registration rejected");
      fetchPendingUsers();
    } catch (error) {
      toast.error("Failed to reject user");
    }
  };

  const handleEditPassword = (user) => {
    setEditPasswordUser(user);
    setNewPassword(user.requested_password || "");
    setPasswordDialogOpen(true);
  };

  const handleSavePassword = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/users/${editPasswordUser.id}`,
        { requested_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Password updated");
      setPasswordDialogOpen(false);
      fetchPendingUsers();
    } catch (error) {
      toast.error("Failed to update password");
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/agents/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Agent deleted");
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete agent");
    }
  };

  if (loading) {
    return <div style={{ color: '#B0B0B0' }}>Loading...</div>;
  }

  // Staff user view - show their own costs
  if (isStaff) {
    return (
      <div data-testid="staff-costs-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>My Flight Costs</h1>
          <p style={{ color: '#B0B0B0' }}>View your assigned flights and payment status</p>
        </div>

        {staffCostsLoading ? (
          <div style={{ color: '#B0B0B0' }}>Loading your costs...</div>
        ) : !staffCosts ? (
          <Card style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
            <CardContent className="p-6 text-center">
              <Users className="h-12 w-12 mx-auto mb-4" style={{ color: '#B0B0B0' }} />
              <p style={{ color: '#B0B0B0' }}>No agent profile linked to your account.</p>
              <p className="text-sm mt-2" style={{ color: '#6B7280' }}>Please contact admin to link your agent profile.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <Card style={{ background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
                <CardContent className="p-4 text-center">
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>Cost per Flight</div>
                  <div className="text-xl font-bold" style={{ color: '#60A5FA' }}>
                    €{staffCosts.cost_per_flight?.toFixed(2) || '0.00'}
                  </div>
                </CardContent>
              </Card>
              <Card style={{ background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
                <CardContent className="p-4 text-center">
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>To Be Paid</div>
                  <div className="text-xl font-bold" style={{ color: '#A855F7' }}>
                    €{staffCosts.total_to_be_paid?.toFixed(2) || '0.00'}
                  </div>
                </CardContent>
              </Card>
              <Card style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                <CardContent className="p-4 text-center">
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>Paid</div>
                  <div className="text-xl font-bold" style={{ color: '#22C55E' }}>
                    €{staffCosts.total_paid?.toFixed(2) || '0.00'}
                  </div>
                </CardContent>
              </Card>
              <Card style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                <CardContent className="p-4 text-center">
                  <div className="text-sm" style={{ color: '#B0B0B0' }}>Remaining</div>
                  <div className="text-xl font-bold" style={{ color: staffCosts.total_difference > 0 ? '#EF4444' : '#22C55E' }}>
                    €{staffCosts.total_difference?.toFixed(2) || '0.00'}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filter Button */}
            <div className="flex gap-2 mb-4">
              {!hidePaidFlights ? (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setHidePaidFlights(true)}
                  style={{ borderColor: '#22C55E', color: '#22C55E' }}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Hide Paid Flights
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setHidePaidFlights(false)}
                  style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Show All Flights
                </Button>
              )}
              <span className="text-sm self-center" style={{ color: '#B0B0B0' }}>
                Showing {getFilteredStaffFlights().length} of {staffCosts.total_flights} flights
              </span>
            </div>

            {/* Flights Table - Read Only */}
            <Card style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="grid grid-cols-7 gap-2 px-4 py-2 text-sm font-medium" style={{ color: '#B0B0B0', borderBottom: '1px solid #4A5A8A' }}>
                    <div>Flight</div>
                    <div>Date</div>
                    <div>Time</div>
                    <div>Route</div>
                    <div className="text-right">To Be Paid</div>
                    <div className="text-right">Paid</div>
                    <div className="text-right">Status</div>
                  </div>
                  
                  {getFilteredStaffFlights().length === 0 ? (
                    <p className="text-center py-4" style={{ color: '#B0B0B0' }}>
                      {hidePaidFlights ? 'All flights are paid!' : 'No flights assigned yet.'}
                    </p>
                  ) : (
                    getFilteredStaffFlights().map((flight) => (
                      <div 
                        key={flight.flight_id} 
                        className="grid grid-cols-7 gap-2 px-4 py-3 rounded items-center"
                        style={{ 
                          background: flight.difference === 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(10, 17, 40, 0.6)',
                          border: `1px solid ${flight.difference === 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(74, 90, 138, 0.3)'}`
                        }}
                      >
                        <div style={{ color: '#FFFFFF' }} className="font-medium">
                          <Plane className="h-3 w-3 inline mr-1" />
                          {flight.flight_number}
                        </div>
                        <div style={{ color: '#E8E8E8' }}>{formatDateDisplay(flight.flight_date)}</div>
                        <div style={{ color: '#B0B0B0' }}>{formatTimeDisplay(flight.flight_time)}</div>
                        <div style={{ color: '#B0B0B0' }}>
                          {flight.departure_airport || '?'} → {flight.arrival_airport || '?'}
                        </div>
                        <div className="text-right font-medium" style={{ color: flight.is_fixed ? '#FFD700' : '#60A5FA' }}>
                          €{flight.to_be_paid?.toFixed(2) || '0.00'}
                          {flight.is_fixed && <Lock className="h-3 w-3 inline ml-1" style={{ color: '#FFD700' }} />}
                        </div>
                        <div className="text-right font-medium" style={{ color: '#22C55E' }}>
                          €{flight.paid_amount?.toFixed(2) || '0.00'}
                        </div>
                        <div className="text-right">
                          {flight.difference === 0 ? (
                            <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22C55E' }}>
                              ✓ Paid
                            </span>
                          ) : (
                            <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#EF4444' }}>
                              €{flight.difference?.toFixed(2)} due
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  }

  // Admin view - original content
  return (
    <div data-testid="agents-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>Agents on Duty</h1>
          <p style={{ color: '#B0B0B0' }}>Manage agents and their duty schedules</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button style={{ background: '#FFFFFF', color: '#0A1128' }} data-testid="add-agent-button">
              <Plus className="mr-2 h-4 w-4" />
              Add Agent
            </Button>
          </DialogTrigger>
          <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
            <DialogHeader>
              <DialogTitle style={{ color: '#FFFFFF' }}>Add New Agent</DialogTitle>
              <DialogDescription style={{ color: '#B0B0B0' }}>Register a new agent</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" style={{ color: '#E8E8E8' }}>Agent Name *</Label>
                <Input id="name" data-testid="agent-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div>
                <Label htmlFor="phone" style={{ color: '#E8E8E8' }}>Phone *</Label>
                <Input id="phone" data-testid="agent-phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
              </div>
              <div>
                <Label htmlFor="email" style={{ color: '#E8E8E8' }}>Email * (for user account)</Label>
                <Input id="email" data-testid="agent-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} />
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Agent will appear in User Management with this email</p>
              </div>
              <div>
                <Label htmlFor="password" style={{ color: '#E8E8E8' }}>
                  Password {editingAgent ? '(leave empty to keep current)' : '*'}
                </Label>
                <Input 
                  id="password" 
                  type="password" 
                  value={formData.password} 
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                  required={!editingAgent}
                  minLength={6}
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
                <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Minimum 6 characters</p>
              </div>
              
              <div>
                <Label htmlFor="role" style={{ color: '#E8E8E8' }}>Role *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value, agency_id: value === 'staff' ? '' : formData.agency_id })}>
                  <SelectTrigger id="role" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                    <SelectItem value="staff">Staff (Company Employee)</SelectItem>
                    <SelectItem value="agency">Agency (External Partner)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.role === 'agency' && (
                <div>
                  <Label htmlFor="agency_id" style={{ color: '#E8E8E8' }}>Select Agency *</Label>
                  <Select value={formData.agency_id} onValueChange={(value) => setFormData({ ...formData, agency_id: value })}>
                    <SelectTrigger id="agency_id" style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}>
                      <SelectValue placeholder="Choose agency" />
                    </SelectTrigger>
                    <SelectContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
                      {agencies.filter(a => a.is_active !== false).map((agency) => (
                        <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label htmlFor="cost_per_flight" style={{ color: '#E8E8E8' }}>Cost per Flight (€)</Label>
                <Input 
                  id="cost_per_flight" 
                  type="number" 
                  step="0.01"
                  placeholder="50.00"
                  value={formData.cost_per_flight} 
                  onChange={(e) => setFormData({ ...formData, cost_per_flight: e.target.value })} 
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }} 
                />
              </div>
              {editingAgent && currentUser.role === 'admin' && (
                <div className="pt-2" style={{ borderTop: '1px solid rgba(255, 215, 0, 0.3)' }}>
                  <Label htmlFor="password" style={{ color: '#FFD700' }}>Change Password (Optional)</Label>
                  <Input 
                    id="password" 
                    type="text"
                    placeholder="Enter new password or leave empty"
                    value={formData.password} 
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                    style={{ background: '#0A1128', borderColor: '#FFD700', color: '#FFD700', fontWeight: 'bold' }} 
                  />
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>Leave empty to keep current password</p>
                </div>
              )}
              <Button type="submit" className="w-full" style={{ background: '#FFFFFF', color: '#0A1128' }} data-testid="submit-agent">
                {editingAgent ? "Update Agent" : "Create Agent"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pending Staff Registrations */}
      {currentUser.role === 'admin' && pendingUsers.length > 0 && (
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFD700' }}>Pending Staff Registrations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pendingUsers.map((user) => (
              <Card key={user.id} className="card-hover" style={{ background: 'rgba(255, 215, 0, 0.1)', border: '2px solid #FFD700', backdropFilter: 'blur(12px)' }}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Users className="mr-2 h-5 w-5" style={{ color: '#FFD700' }} />
                      <span style={{ color: '#E8E8E8' }}>{user.full_name}</span>
                    </div>
                    <span className="text-xs px-2 py-1 rounded" style={{ background: '#FFD700', color: '#0A1128' }}>PENDING</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center text-sm" style={{ color: '#B0B0B0' }}>
                    <Mail className="mr-2 h-4 w-4" style={{ color: '#FFD700' }} />
                    {user.email}
                  </div>
                  <div className="text-sm">
                    <span className="font-medium" style={{ color: '#E8E8E8' }}>Role: </span>
                    <span className="capitalize" style={{ color: '#B0B0B0' }}>{user.role}</span>
                  </div>
                  {user.requested_password && (
                    <div className="mt-2 p-2 rounded" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-semibold" style={{ color: '#FFD700' }}>Requested Password:</div>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleEditPassword(user)}
                          style={{ color: '#60A5FA', padding: '2px 8px', height: 'auto' }}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      </div>
                      <div className="text-sm font-mono" style={{ color: '#E8E8E8' }}>{user.requested_password}</div>
                    </div>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button 
                      size="sm" 
                      onClick={() => handleApproveUser(user)}
                      style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', border: '1px solid #22c55e' }}
                      className="flex-1"
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => handleRejectUser(user.id)}
                      style={{ background: 'rgba(239, 68, 68, 0.2)', color: '#ef4444', border: '1px solid #ef4444' }}
                      className="flex-1"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Active Agents - Can Be Assigned on Duty */}
      <h2 className="text-2xl font-bold mb-4" style={{ color: '#FFD700' }}>Active Agents (Can Be Assigned on Duty)</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map((agent) => {
          const agencyName = agent.agency_id ? agencies.find(a => a.id === agent.agency_id)?.name : null;
          const isStaff = agent.role === 'staff' || !agent.role;
          const flightCount = agentFlightCounts[agent.id] || 0;
          const canDelete = flightCount === 0;
          
          return (
            <Card key={agent.id} className="card-hover" style={{ 
              background: 'rgba(30, 42, 74, 0.6)', 
              border: `1px solid ${isStaff ? 'rgba(255, 215, 0, 0.3)' : 'rgba(168, 85, 247, 0.3)'}` 
            }} data-testid={`agent-card-${agent.id}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Users className="mr-2 h-5 w-5" style={{ color: isStaff ? '#FFD700' : '#A855F7' }} />
                    <span style={{ color: '#E8E8E8' }}>{agent.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 rounded" style={{ 
                      background: isStaff ? 'rgba(255, 215, 0, 0.2)' : 'rgba(168, 85, 247, 0.2)', 
                      color: isStaff ? '#FFD700' : '#A855F7' 
                    }}>
                      {agent.role || 'staff'}
                    </span>
                    {currentUser.role === 'admin' && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(agent)} style={{ color: '#60A5FA' }}>
                          Edit
                        </Button>
                        {canDelete ? (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAgent(agent.id)} style={{ color: '#FF6B6B' }}>
                            Delete
                          </Button>
                        ) : (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            disabled 
                            title={`Cannot delete: ${flightCount} flight(s) assigned`}
                            style={{ color: '#6B7280', cursor: 'not-allowed' }}
                          >
                            <Lock className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center text-sm" style={{ color: '#B0B0B0' }}>
                  <Phone className="mr-2 h-4 w-4" style={{ color: '#FFFFFF' }} />
                  {agent.phone}
                </div>
                {agent.email && (
                  <div className="flex items-center text-sm" style={{ color: '#B0B0B0' }}>
                    <Mail className="mr-2 h-4 w-4" style={{ color: '#FFFFFF' }} />
                    {agent.email}
                  </div>
                )}
                {agencyName && (
                  <div className="text-xs p-2 rounded mt-2" style={{ background: 'rgba(168, 85, 247, 0.1)', color: '#A855F7' }}>
                    Agency: {agencyName}
                  </div>
                )}
                {agent.cost_per_flight && (
                  <div className="flex items-center text-sm mt-2" style={{ color: '#22C55E' }}>
                    <DollarSign className="mr-2 h-4 w-4" />
                    €{agent.cost_per_flight.toFixed(2)} per flight
                  </div>
                )}
                {flightCount > 0 && (
                  <div className="flex items-center text-xs mt-1" style={{ color: '#60A5FA' }}>
                    <Plane className="mr-1 h-3 w-3" />
                    {flightCount} flight(s) assigned
                  </div>
                )}
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" className="flex-1" onClick={() => fetchAgentDuties(agent.id, agent.name)} style={{ borderColor: '#FFFFFF', color: '#FFFFFF' }}>
                    <Calendar className="mr-2 h-4 w-4" />
                    Planning
                  </Button>
                  {currentUser.role === 'admin' && (
                    <Button variant="outline" className="flex-1" onClick={() => fetchAgentCosts(agent.id, agent.name)} style={{ borderColor: '#22C55E', color: '#22C55E' }}>
                      <DollarSign className="mr-2 h-4 w-4" />
                      Agent Cost
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {agents.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-12 w-12 mx-auto mb-4" style={{ color: '#4A5A8A' }} />
          <p style={{ color: '#B0B0B0' }}>No agents found. Add your first agent.</p>
        </div>
      )}

      <Dialog open={dutiesOpen} onOpenChange={setDutiesOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Duty Planning - {selectedAgent}</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Filter duties by date range
            </DialogDescription>
          </DialogHeader>
          
          {/* Filter Checkboxes */}
          <div className="flex items-center gap-6 mb-4 p-3 rounded" style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="past-duties"
                checked={showPastDuties}
                onCheckedChange={(checked) => setShowPastDuties(checked)}
                style={{ borderColor: '#B0B0B0' }}
              />
              <Label htmlFor="past-duties" style={{ color: '#B0B0B0', cursor: 'pointer' }}>Past</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="future-duties"
                checked={showFutureDuties}
                onCheckedChange={(checked) => setShowFutureDuties(checked)}
                style={{ borderColor: '#22C55E' }}
              />
              <Label htmlFor="future-duties" style={{ color: '#22C55E', cursor: 'pointer' }}>Future</Label>
            </div>
            <span className="text-sm ml-auto" style={{ color: '#B0B0B0' }}>
              Showing {getFilteredDuties().length} of {agentDuties.length} duties
            </span>
          </div>

          <div className="space-y-3">
            {agentDuties.length === 0 ? (
              <p style={{ color: '#B0B0B0' }}>No duties assigned yet.</p>
            ) : getFilteredDuties().length === 0 ? (
              <p style={{ color: '#B0B0B0' }}>No duties match the selected filter.</p>
            ) : (
              getFilteredDuties().map((duty, idx) => (
                <Card key={idx} style={{ background: 'rgba(10, 17, 40, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <div style={{ color: '#B0B0B0' }}>Flight</div>
                        <div style={{ color: '#FFFFFF' }} className="font-medium">{duty.flight_number}</div>
                      </div>
                      <div>
                        <div style={{ color: '#B0B0B0' }}>Date</div>
                        <div style={{ color: '#FFFFFF' }}>{duty.flight_date}</div>
                      </div>
                      <div>
                        <div style={{ color: '#B0B0B0' }}>Route</div>
                        <div style={{ color: '#FFFFFF' }}>{duty.departure_airport || 'N/A'} → {duty.arrival_airport || 'N/A'}</div>
                      </div>
                      <div>
                        <div style={{ color: '#B0B0B0' }}>PAX</div>
                        <div style={{ color: '#FFFFFF' }}>{duty.passenger_count || 'N/A'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent Costs Dialog */}
      <Dialog open={costsOpen} onOpenChange={setCostsOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>Agent Cost - {selectedAgent}</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              Cost per flight: €{agentCosts?.cost_per_flight?.toFixed(2) || '0.00'} | 
              Total flights: {agentCosts?.total_flights || 0}
            </DialogDescription>
          </DialogHeader>
          
          {agentCosts && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <Card style={{ background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
                  <CardContent className="p-4 text-center">
                    <div className="text-sm" style={{ color: '#B0B0B0' }}>To Be Paid</div>
                    <div className="text-xl font-bold" style={{ color: '#60A5FA' }}>
                      €{agentCosts.total_to_be_paid?.toFixed(2) || '0.00'}
                    </div>
                  </CardContent>
                </Card>
                <Card style={{ background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                  <CardContent className="p-4 text-center">
                    <div className="text-sm" style={{ color: '#B0B0B0' }}>Paid</div>
                    <div className="text-xl font-bold" style={{ color: '#22C55E' }}>
                      €{agentCosts.total_paid?.toFixed(2) || '0.00'}
                    </div>
                  </CardContent>
                </Card>
                <Card style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                  <CardContent className="p-4 text-center">
                    <div className="text-sm" style={{ color: '#B0B0B0' }}>Difference</div>
                    <div className="text-xl font-bold" style={{ color: agentCosts.total_difference > 0 ? '#EF4444' : '#22C55E' }}>
                      €{agentCosts.total_difference?.toFixed(2) || '0.00'}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Filter Buttons */}
              <div className="flex gap-2 mb-4">
                {!hidePaidFlights ? (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setHidePaidFlights(true)}
                    style={{ borderColor: '#22C55E', color: '#22C55E' }}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Hide Paid Flights
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setHidePaidFlights(false)}
                    style={{ borderColor: '#60A5FA', color: '#60A5FA' }}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    Show All Flights
                  </Button>
                )}
                <span className="text-sm self-center" style={{ color: '#B0B0B0' }}>
                  Showing {getFilteredFlights().length} of {agentCosts.total_flights} flights
                </span>
              </div>

              {/* Flights Table */}
              <div className="space-y-2">
                <div className="grid grid-cols-9 gap-2 px-4 py-2 text-sm font-medium" style={{ color: '#B0B0B0', borderBottom: '1px solid #4A5A8A' }}>
                  <div>Flight</div>
                  <div>Date</div>
                  <div>Time</div>
                  <div>Route</div>
                  <div className="text-right">To Be Paid</div>
                  <div className="text-right">Paid</div>
                  <div className="text-right">Difference</div>
                  <div className="text-center">Fix Price</div>
                  <div></div>
                </div>
                
                {getFilteredFlights().length === 0 ? (
                  <p className="text-center py-4" style={{ color: '#B0B0B0' }}>
                    {hidePaidFlights ? 'No unpaid flights.' : 'No flights assigned yet.'}
                  </p>
                ) : (
                  getFilteredFlights().map((flight) => (
                    <div 
                      key={flight.flight_id} 
                      className="grid grid-cols-9 gap-2 px-4 py-3 rounded items-center"
                      style={{ 
                        background: flight.difference === 0 ? 'rgba(34, 197, 94, 0.1)' : 'rgba(10, 17, 40, 0.6)',
                        border: `1px solid ${flight.difference === 0 ? 'rgba(34, 197, 94, 0.3)' : 'rgba(74, 90, 138, 0.3)'}`
                      }}
                    >
                      <div style={{ color: '#FFFFFF' }} className="font-medium">
                        <Plane className="h-3 w-3 inline mr-1" />
                        {flight.flight_number}
                      </div>
                      <div style={{ color: '#E8E8E8' }}>{formatDateDisplay(flight.flight_date)}</div>
                      <div style={{ color: '#B0B0B0' }}>{formatTimeDisplay(flight.flight_time)}</div>
                      <div style={{ color: '#B0B0B0' }}>
                        {flight.departure_airport || '?'} → {flight.arrival_airport || '?'}
                      </div>
                      <div className="text-right font-medium" style={{ color: flight.is_fixed ? '#FFD700' : '#60A5FA' }}>
                        €{flight.to_be_paid?.toFixed(2) || '0.00'}
                        {flight.is_fixed && <Lock className="h-3 w-3 inline ml-1" style={{ color: '#FFD700' }} />}
                      </div>
                      <div className="text-right">
                        {flight.is_fixed ? (
                          <div 
                            className="w-20 h-8 flex items-center justify-end px-2 rounded"
                            style={{ 
                              background: 'rgba(255, 215, 0, 0.1)', 
                              border: '1px solid #FFD700', 
                              color: '#FFD700',
                              fontSize: '0.875rem'
                            }}
                          >
                            <Lock className="h-3 w-3 mr-1" />
                            {flight.paid_amount?.toFixed(2) || '0.00'}
                          </div>
                        ) : (
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            defaultValue={flight.paid_amount || 0}
                            onBlur={(e) => updatePaidAmount(flight.flight_id, e.target.value)}
                            className="w-20 h-8 text-right"
                            style={{ 
                              background: '#0A1128', 
                              borderColor: '#22C55E', 
                              color: '#22C55E',
                              fontSize: '0.875rem'
                            }}
                          />
                        )}
                      </div>
                      <div className="text-right font-medium" style={{ color: flight.difference > 0 ? '#EF4444' : '#22C55E' }}>
                        €{flight.difference?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleFixPrice(flight.flight_id, flight.is_fixed, flight.to_be_paid)}
                          className="h-7 px-2"
                          style={{ 
                            color: flight.is_fixed ? '#FFD700' : '#B0B0B0',
                            background: flight.is_fixed ? 'rgba(255, 215, 0, 0.1)' : 'transparent'
                          }}
                          title={flight.is_fixed ? 'Unfix price (use current rate)' : 'Fix price (lock current rate)'}
                        >
                          {flight.is_fixed ? (
                            <>
                              <Lock className="h-3 w-3 mr-1" />
                              Fixed
                            </>
                          ) : (
                            <>
                              <Unlock className="h-3 w-3 mr-1" />
                              Fix
                            </>
                          )}
                        </Button>
                      </div>
                      <div className="text-center">
                        {flight.difference === 0 && (
                          <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22C55E' }}>
                            ✓ Paid
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Edit Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFD700' }}>Edit Password</DialogTitle>
            <DialogDescription style={{ color: '#B0B0B0' }}>
              {editPasswordUser && `Editing password for ${editPasswordUser.full_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-password" style={{ color: '#E8E8E8' }}>Password</Label>
              <Input
                id="new-password"
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{ background: '#0A1128', borderColor: '#FFD700', color: '#FFD700', fontWeight: 'bold' }}
              />
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={handleSavePassword}
                className="flex-1"
                style={{ background: '#22c55e', color: '#FFFFFF' }}
              >
                Save Password
              </Button>
              <Button 
                onClick={() => setPasswordDialogOpen(false)}
                variant="outline"
                className="flex-1"
                style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Agents;
